

import React, { useEffect, useState, useMemo } from "react";
import memoize from "lodash.memoize";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import Tooltip from "@mui/material/Tooltip";
import AutoCompleteDropdown from "components/autocomplete/AutoCompleteDropdown";
import DataTable from "pages/components/DataTable";
import { sendAPIRequest, generateOptionsFromData, displayNotification } from "helpers";
import { setCurrentPage } from "redux/generalSlice";
import Crumbs from "components/crumbs/Crumbs";
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;

const initialFilters = {
  country_id: [],
  lang_id: [],
  expertise: [],
  expert_name: [],
  mission_country_id: [],
  country_of_residence: []
};

const LeadsManagement = () => {

  const dispatch = useDispatch();
  const authData = useSelector(s => s.auth.data);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const crumbs = [
    {
      path: "/portal/",
      label: t("-1", "Home"),
      active: false,
    },
    {
      path: "/portal/leads-management",
      label: t("-1", "LeadsManagement"),
      active: true,
    },
  ];
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [country, setCountry] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [expertName, setExpertName] = useState([]);
  const [expertise, setExpertise] = useState([
    { id: "pvs-expert", name: "PVS Expert" },
    { id: "aqua-expert", name: "Aqua Expert" },
    { id: "gap-expert", name: "GAP Expert" },
    { id: "legislation-expert", name: "Legislation Expert" },
    { id: "laboratory-expert", name: "Laboratory Expert" },
  ]);
  const [returnParams, setReturnParams] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const lang_id = searchParams.get('lang_id');
  const country_id = searchParams.get('country_id');
  const country_of_residence = searchParams.get('country_of_residence');
  const mission_country_id = searchParams.get('mission_country_id');
  const get_expertise = searchParams.get('expertise');
  const expert_name = searchParams.get('expert_name');

  const [filters, setFilters] = useState(
    {
      ...initialFilters,
      lang_id: lang_id ? lang_id.split(',').map(v => parseInt(v)) : [],
      expertise: get_expertise ? get_expertise.split(',').map(v => parseInt(v)) : [],
      country_id: country_id ? country_id.split(',').map(v => parseInt(v)) : [],
      country_of_residence: country_of_residence ? country_of_residence.split(',').map(v => parseInt(v)) : [],
      mission_country_id: mission_country_id ? mission_country_id.split(',').map(v => parseInt(v)) : [],
      expert_name: expert_name ? expert_name.split(',').map(v => parseInt(v)) : [],
    }
  );

  useEffect(() => {
    dispatch(setCurrentPage("experts-management"));
    loadCountries();
    loadLanguages();
    loadData();
    loadExpertNames();
  }, []);

  const goBack = () => {
    navigate(-1);
  }

  const countryOptions = useMemo(() => {
    return generateOptionsFromData(country, {
      value: "id",
      label: "country_complete_name",
    });
  }, [country]);
  const languageOptions = useMemo(() => {
    return generateOptionsFromData(languages, {
      value: "id",
      label: "list_element_name",
    });
  }, [languages]);
  const expertNameOptions = useMemo(() => {
    return generateOptionsFromData(expertName, {
      value: "id",
      label: "expert_name",
    });
  }, [expertName]);
  const expertiseOptions = useMemo(() => {
    return generateOptionsFromData(expertise, {
      value: "id",
      label: "name",
    });
  }, [expertise]);

  const navigationFunction = (key, value) => {
    let params = { ...filters };
    params[key] = value;
    setFilters({
      ...params,
    });
    params.country_id = params.country_id.join(",");
    params.lang_id = params.lang_id.join(",");
    params.expertise = params.expertise.join(",");
    params.expert_name = params.expert_name.join(",");
    params = new URLSearchParams(params).toString();
    let temp = params;
    temp = temp.replaceAll('&', '|');
    setReturnParams(temp);
    navigate(`/portal/experts-management/?${params}`);
  }

  const loadData = async (chk = true) => {
    try {
      setIsLoading(true);
      let fltr = { ...filters };
      if (chk == false) {
        fltr = initialFilters;
      }
      let params = new URLSearchParams();
      fltr.country_id.forEach((element) => {
        params.append("country_id", element);
      });
      fltr.country_of_residence.forEach((element) => {
        params.append("country_of_residence", element);
      });
      fltr.lang_id.forEach((element) => {
        params.append("lang_id", element);
      });
      fltr.expertise.forEach((element) => {
        params.append("expertise", element);
      });
      fltr.expert_name.forEach((element) => {
        params.append("person_id", element);
      });
      fltr.mission_country_id.forEach((element) => {
        params.append("mission_country_id", element);
      });
      if (authData && authData.id) {
        params.append("user_id", authData.id);
      }
      // const req = await sendAPIRequest(`/api/expert?${params}`);
      const req = await sendAPIRequest(`/api/expert/load_experts?${params}`);
      const res = await req.json();
      if (res.success) {
        console.log({ res });
        setData([...res.data]);
        setIsLoading(false);
      } else {
        // displayNotification("error", `${t("222", "Error loading data")} [B]`);
      }
    } catch (e) {
      console.log(e);
      // displayNotification("error", `${t("222", "Error loading data")} [F]`);
    }
  };

  const loadCountries = async () => {
    try {
      const req = await sendAPIRequest(`/api/country`);
      const res = await req.json();
      if (res.success) {
        setCountry(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadExpertNames = async () => {
    try {
      const req = await sendAPIRequest(`/api/expert/full-name`);
      const res = await req.json();
      if (res.success) {
        setExpertName([...res.data]);
      } else {
        // displayNotification("error", `${t("222", "Error loading data")} [B]`);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadLanguages = async () => {
    try {
      const req = await sendAPIRequest(`/api/list/master/3`);
      const res = await req.json();
      if (res.success) {
        setLanguages([...res.data]);
      } else {
        // displayNotification("error", `${t("222", "Error loading data")} [B]`);
      }
    } catch (e) {
      console.log(e);
      // displayNotification("error", `${t("222", "Error loading data")} [F]`);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm(t("243", "Are you sure you want to delete?"))) {
        return;
      }
      const req = await sendAPIRequest(`/api/expert/${id}`, "DELETE");
      const res = await req.json();
      if (res.success) {
        displayNotification('success', `${t("244", "Successfully deleted element data")} [B]`);
        loadData();
      } else {
        displayNotification('error', `${t("245", "Error deleting element data")} [B]`);
      }
    } catch (e) {
      console.log(e);
      displayNotification('error', `${t("245", "Error deleting element data")} [F]`);
    }
  };
  const resetFilter = async () => {
    setFilters({ ...initialFilters });
    loadData(false);
  }
  const navigateToMission = (mission_id) => {
    window.open(`/portal/mission-management/${mission_id}/view/?return=/portal/experts-management/?`, '_blank', 'rel=noopener noreferrer')
  }

  return <>
    <div className="container my-2">
      <Crumbs crumbs={crumbs} />
      <div className="header card-woah border border-secondary d-flex btn-res1 px-4 py-2 shadow-sm w-100 my-2">
        <div className="card-title1 lh-1">{t("-1", "Leads Management")}</div>
        <div className="header-right-items d-flex align-items-center ms-auto">
          <div className="ms-auto">
            <Link to={"/portal/"} className="ms-1">
              <button className="btn btn--primary fs-7 lh-1">
                {" "}
                <i className="fas fa-angle-left text-white" />
                {t("32", "Back")}
              </button>
            </Link>
            <Link to={"/portal/leads-management/create"} className="ms-1">
              <button className="btn btn--outline fs-7 lh-1">
                <span className="align-middle">{t("-1", "Add New Leads")}</span>
                <i className="fas fa-plus align-middle text-black ms-2" />
              </button>
            </Link>
          </div>

        </div>
      </div>
      <div className="card card-woah border border-secondary shadow rounded-0 mb-1 ">
        <div className="card-header px-4 py-2 min-h-10px">
          <div className="card-title my-0">{t("14", "Filters")}</div>
        </div>
        <div className="card-body p-4">
          <div className="row ">
            <div className="col-md-3 col-12 mb-2">
              <Tooltip
                title={t("667", "You can select multiple options")}
                placement="top"
                arrow
              >
                <AutoCompleteDropdown
                  error={false}
                  required={false}
                  label={t("2917", "Nationality")}
                  multiple={true}
                  hasselectall={true}
                  options={countryOptions}
                  onChange={(event, value) => {
                    if (value.some((v) => v.value == "Select all")) {
                      if (filters.country_id.length == countryOptions.length) {
                        // Deselect all options
                        navigationFunction("country_id", []);
                      } else {
                        // Select all options
                        navigationFunction("country_id", countryOptions.map((r) => r.value));
                      }
                    } else {
                      navigationFunction("country_id", value.map((r) => r.value));
                    }
                  }}
                  value={filters.country_id}
                />
              </Tooltip>
            </div>
            <div className="col-md-3 col-12 mb-2">
              <Tooltip
                title={t("667", "You can select multiple options")}
                placement="top"
                arrow
              >
                <AutoCompleteDropdown
                  error={false}
                  required={false}
                  label={t("2887", "Country Missions")}
                  multiple={true}
                  hasselectall={true}
                  options={countryOptions}
                  onChange={(event, value) => {
                    if (value.some((v) => v.value == "Select all")) {
                      if (filters.mission_country_id.length == countryOptions.length) {
                        // Deselect all options
                        navigationFunction("mission_country_id", []);
                      } else {
                        // Select all options
                        navigationFunction("mission_country_id", countryOptions.map((r) => r.value));
                      }
                    } else {
                      navigationFunction("mission_country_id", value.map((r) => r.value));
                    }
                  }}
                  value={filters.mission_country_id}
                />
              </Tooltip>
            </div>
            <div className="col-md-3 col-12 mb-2">
              <Tooltip
                title={t("667", "You can select multiple options")}
                placement="top"
                arrow
              >
                <AutoCompleteDropdown
                  error={false}
                  required={false}
                  label={t("19", "All Languages")}
                  multiple={true}
                  hasselectall={true}
                  options={languageOptions}
                  onChange={(event, value) => {
                    if (value.some((v) => v.value == "Select all")) {
                      if (filters.lang_id.length == languageOptions.length) {
                        // Deselect all options
                        navigationFunction("lang_id", []);
                      } else {
                        // Select all options
                        navigationFunction("lang_id", languageOptions.map((r) => r.value));
                      }
                    } else {
                      navigationFunction("lang_id", value.map((r) => r.value));
                    }
                  }}
                  value={filters.lang_id}
                />
              </Tooltip>
            </div>
            <div className="col-md-3 col-12 mb-2">
              <Tooltip
                title={t("667", "You can select multiple options")}
                placement="top"
                arrow
              >
                <AutoCompleteDropdown
                  error={false}
                  required={false}
                  label={t("-1", "All Leads")}
                  multiple={true}
                  hasselectall={true}
                  options={expertiseOptions}
                  onChange={(event, value) => {
                    if (value.some((v) => v.value == "Select all")) {
                      if (filters.expertise.length == expertiseOptions.length) {
                        // Deselect all options
                        navigationFunction("expertise", []);
                      } else {
                        // Select all options
                        navigationFunction("expertise", expertiseOptions.map((r) => r.value));
                      }
                    } else {
                      navigationFunction("expertise", value.map((r) => r.value));
                    }
                  }}
                  value={filters.expertise}
                />
              </Tooltip>
            </div>

            <div className="col-md-3 col-12 mb-2">
              <Tooltip
                title={t("667", "You can select multiple options")}
                placement="top"
                arrow
              >
                <AutoCompleteDropdown
                  error={false}
                  required={false}
                  label={t("-1", "Leads Name")}
                  multiple={true}
                  hasselectall={true}
                  options={expertNameOptions}
                  onChange={(event, value) => {
                    if (value.some((v) => v.value == "Select all")) {
                      if (filters.expert_name.length == expertNameOptions.length) {
                        // Deselect all options
                        navigationFunction("expert_name", []);
                      } else {
                        // Select all options
                        navigationFunction("expert_name", expertNameOptions.map((r) => r.value));
                      }
                    } else {
                      navigationFunction("expert_name", value.map((r) => r.value));
                    }
                  }}
                  value={filters.expert_name}
                />
              </Tooltip>
            </div>
            <div className="col-md-9 col-sm-6 col-12 mt-2 text-end">
              <button
                className="btn btn--outline fs-7 lh-1 mb-2 ms-2"
                type="button"
                onClick={() => resetFilter()}
              >
                <span className="align-middle">{t("258", "Reset")}</span>
                <i className="fas fa-arrow-rotate-left text-black ms-2" />
              </button>
              <button
                className="btn btn--primary fs-7 lh-0 mb-2 ms-1"
                type="button"
                onClick={() => loadData()}
              >
                <span className="align-middle">{t("21", "Search")}</span>
                <i className="fas fa-search text-white ms-2 " />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card card-woah border border-secondary shadow rounded-0">
        <div className="card-body p-4">
          <DataTable
            // getRowId={(row) => `custom-${Math.random().toString(36).substr(2, 9)}-${row.id}`}
            isLoading={isLoading}
            rows={data}
            className="text-white bg-primary rounded-0"
            getRowId={(row) => row.first_name + "-" + row.last_name + "-" + row.mission_id}
            columns={[
              {
                field: "first_name",
                headerName: t("192", "First Name"),
                headerClassName: "text-white bg-primary rounded-0",
                minWidth: 100,
                flex: 0,
              },
              {
                field: "last_name",
                headerName: t("193", "Last Name"),
                headerClassName: "text-white bg-primary rounded-0",
                minWidth: 100,
                flex: 0,
              },
              {
                field: "country_name",
                headerName: t("2917", "Nationality"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 0,
              },
              {
                field: "language",
                headerName: t("26", "Language"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 1,
                valueGetter: (params) =>
                  params.row.languages.map(i => i.name).join(","),
              },
              {
                field: "missions",
                headerName: t("646", "Missions"),
                headerClassName: "text-white bg-primary rounded-0",
                minWidth: 350,
                renderCell: (params) => {
                  return (
                    <div className="row">
                      {Array.isArray(params?.row?.mission) ? params?.row?.mission.map((i, j) => {
                        return <a href="#" className="col-12" onClick={() => navigateToMission(i.id)}>{i.name}</a>;
                      }) : ""
                      }
                    </div>
                  );
                },
                flex: 0,
              },
              {
                field: "expert_areas",
                headerName: t("40", "Expertise"),
                headerClassName: "text-white bg-primary rounded-0",
                flex: 3,
                renderCell: (params) => (
                  <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {params.row.expert_areas.map((area, index) => (
                      <div className="col-6 mb-1">
                        <span
                          className={
                            `badge rounded-3 p-2 ${area === 'PVS Expert' && 'bg-green'} ${area === 'Aqua Expert' && 'bg-light-blue'} ${area === 'Gap Expert' && 'bg-light-orange'} ${area === 'Legislation Expert' && 'bg-light-alice-blue'} ${area === 'Laboratory Expert' && 'bg-light-gray'}`
                          }
                          style={{ fontSize: "0.875rem" }}>
                          <div key={index}>{area}</div>
                        </span>
                      </div>
                    ))}
                  </div>
                ),
                // renderCell: (params) => {
                //   return (
                //     <div className="row no-gutters">
                //       {params.row.is_pvs_expert == 1 ? (
                //         <div className="col-6 mb-1">
                //           <span className="badge rounded-3 bg-green p-2" style={{ fontSize: "0.875rem" }}>
                //             {t("194", "PVS Expert")}{" "}
                //           </span>
                //         </div>
                //       ) : (
                //         ""
                //       )}
                //       {params.row.is_aqua_expert == 1 ? (
                //         <div className="col-6 mb-1">
                //           <span className="badge rounded-3 bg-light-blue p-2" style={{ fontSize: "0.875rem" }}>
                //             {t("195", "Aqua Expert")}{" "}
                //           </span>
                //         </div>
                //       ) : (
                //         ""
                //       )}
                //       {params.row.is_gap_expert == 1 ? (
                //         <div className="col-6 mb-1">
                //           <span className="badge rounded-3 bg-light-orange p-2" style={{ fontSize: "0.875rem" }}>
                //             {t("196", "GAP Expert")}{" "}
                //           </span>
                //         </div>
                //       ) : (
                //         ""
                //       )}
                //       {params.row.is_legislation_expert == 1 ? (
                //         <div className="col-6 mb-1">
                //           <span className="badge rounded-3 bg-light-alice-blue p-2" style={{ fontSize: "0.875rem" }}>
                //             {t("197", "Legislation Expert")}{" "}
                //           </span>
                //         </div>
                //       ) : (
                //         ""
                //       )}
                //       {params.row.is_laboratory_expert == 1 ? (
                //         <div className="col-4">
                //           <span className="badge rounded-3 bg-light-gray p-2" style={{ fontSize: "0.875rem" }}>
                //             {t("198", "Laboratory Expert")}{" "}
                //           </span>
                //         </div>
                //       ) : (
                //         ""
                //       )}
                //     </div>
                //   );
                // },
              },
              {
                field: "mission_count",
                headerName: `# ${t("965", "of Past Missions Conducted")}`,
                headerClassName: "text-white bg-primary rounded-0",
                flex: 1,
              },
              {
                field: "actions",
                type: "actions",
                headerClassName: "text-white bg-primary rounded-0",
                headerName: t("74", "Actions"),
                getActions: (params) => [
                  ...(params.row.can_view == true
                    ? [
                      <GridActionsCellItem
                        label={t("251", "View")}
                        key={0}
                        showInMenu
                        onClick={() => {
                          const url = `/portal/experts-management/?${returnParams.length > 0 ? returnParams : ''}`;
                          return navigate(
                            `/portal/experts-management/${params.row.id}/view/?return=${url}`
                          );
                        }
                        }
                      />,
                    ]
                    : []),
                  ...(params.row.can_edit == true
                    ? [
                      <GridActionsCellItem
                        label={t("80", "Edit")}
                        key={0}
                        showInMenu
                        onClick={() => {
                          const url = `/portal/experts-management/?${returnParams.length > 0 ? returnParams : ''}`;
                          return navigate(
                            `/portal/experts-management/${params.row.id}/edit/?return=${url}`
                          );
                        }
                        }
                      />,
                    ]
                    : []),

                  ...(params.row.can_delete == true
                    ? [
                      <GridActionsCellItem
                        label={t("29", "Delete")}
                        key={1}
                        showInMenu
                        onClick={() => handleDelete(params.row.id)}
                      />,
                    ]
                    : []),
                ],
                width: 90,
              },
            ]}
          />
        </div>
      </div>
    </div>
  </>
}

export default LeadsManagement;
